import { useEffect } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useGetHomePermissions, useRequiresAuthorization } from '@ibk/shared-permissioning';
import { ApiProductStatus, AppDomain, FeatureFlagUseCase, PermissionsDomains, PermissionsResources, ProductAlias, } from '@ibk/shared-types';
import { useGetAccountantEmissorHomeBenefitsProductQuery, useGetClientEmissorHomeBenefitsProductQuery, } from '../services/home/emissorHomeProductApi';
import { normalizeProductResponse, productApi } from '../services/home/productApi';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectAppDomain } from '../store/selectors/applicationSelector';
import { selectAllProducts, selectHasAnyProductError, selectIsAnyProductLoading, } from '../store/selectors/productsSelectors';
import { selectSelectedAccountId, selectSelectedCompanyDocument, selectSelectedCompanyOrgId, } from '../store/selectors/userSelectors';
import { setProductData, setProductError, setProductLoading } from '../store/slices/productsSlice';
export const useHomeProducts = ({ enabled = true } = {}) => {
    const dispatch = useAppDispatch();
    const appDomain = useAppSelector(selectAppDomain);
    const selectedAccountId = useAppSelector(selectSelectedAccountId);
    const selectedCompanyOrgId = useAppSelector(selectSelectedCompanyOrgId);
    const selectedCompanyDocument = useAppSelector(selectSelectedCompanyDocument);
    const products = useAppSelector(selectAllProducts);
    const isLoading = useAppSelector(selectIsAnyProductLoading);
    const hasError = useAppSelector(selectHasAnyProductError);
    const { hasDigitalAccountingPermission, hasBenefitPermission, hasProgramaFidelidadePermission, hasBillingPermission, } = useGetHomePermissions();
    const canSeeBalance = useRequiresAuthorization(PermissionsDomains.ACCOUNT, [
        PermissionsResources.ACCOUNT_VIEW_BALANCE,
    ]);
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const isEmissorEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_EMISSOR_ENABLED);
    const queryOptions = { refetchOnMountOrArgChange: true, refetchOnFocus: false, refetchOnReconnect: true };
    const skipContaDigital = !enabled || !selectedAccountId;
    const skipCobrancas = !enabled || !selectedAccountId || !hasBillingPermission;
    const skipContabilidade = !enabled || !selectedAccountId || !hasDigitalAccountingPermission;
    const skipFidelidade = !enabled || !selectedAccountId || !hasProgramaFidelidadePermission || appDomain !== AppDomain.ESCRITORIO;
    const skipLegacyBeneficios = !enabled || !selectedAccountId || !selectedCompanyOrgId || !hasBenefitPermission;
    const contaDigitalParams = {
        productAlias: ProductAlias.CONTA_DIGITAL,
        selectedCompanyPartyId: selectedAccountId || '',
        accountPjCanSeeBalance: canSeeBalance,
    };
    const cobrancasParams = { productAlias: ProductAlias.COBRANCAS, selectedCompanyPartyId: selectedAccountId || '' };
    const contabilidadeParams = {
        productAlias: ProductAlias.CONTABILIDADE_DIGITAL,
        selectedCompanyPartyId: selectedAccountId || '',
    };
    const fidelidadeParams = {
        productAlias: 'fidelity_program',
        selectedCompanyPartyId: selectedAccountId || '',
    };
    const beneficiosParams = {
        productAlias: ProductAlias.BENEFICIOS,
        selectedCompanyPartyId: selectedAccountId || '',
        selectedCompanyOrgId: selectedCompanyOrgId || undefined,
    };
    const contaDigitalV1 = productApi.useGetHomeProductQuery(contaDigitalParams, {
        ...queryOptions,
        skip: skipContaDigital || isV2Enabled,
    });
    const contaDigitalV2 = productApi.useGetHomeProductV2Query(contaDigitalParams, {
        ...queryOptions,
        skip: skipContaDigital || !isV2Enabled,
    });
    const contaDigitalQuery = isV2Enabled ? contaDigitalV2 : contaDigitalV1;
    const cobrancasV1 = productApi.useGetHomeProductQuery(cobrancasParams, {
        ...queryOptions,
        skip: skipCobrancas || isV2Enabled,
    });
    const cobrancasV2 = productApi.useGetHomeProductV2Query(cobrancasParams, {
        ...queryOptions,
        skip: skipCobrancas || !isV2Enabled,
    });
    const cobrancasQuery = isV2Enabled ? cobrancasV2 : cobrancasV1;
    const contabilidadeV1 = productApi.useGetHomeProductQuery(contabilidadeParams, {
        ...queryOptions,
        skip: skipContabilidade || isV2Enabled,
    });
    const contabilidadeV2 = productApi.useGetHomeProductV2Query(contabilidadeParams, {
        ...queryOptions,
        skip: skipContabilidade || !isV2Enabled,
    });
    const contabilidadeQuery = isV2Enabled ? contabilidadeV2 : contabilidadeV1;
    const fidelidadeV1 = productApi.useGetHomeProductQuery(fidelidadeParams, {
        ...queryOptions,
        skip: skipFidelidade || isV2Enabled,
    });
    const fidelidadeV2 = productApi.useGetHomeProductV2Query(fidelidadeParams, {
        ...queryOptions,
        skip: skipFidelidade || !isV2Enabled,
    });
    const programaFidelidadeQuery = isV2Enabled ? fidelidadeV2 : fidelidadeV1;
    const beneficiosV1 = productApi.useGetHomeProductQuery(beneficiosParams, {
        ...queryOptions,
        skip: skipLegacyBeneficios || isV2Enabled,
    });
    const beneficiosV2 = productApi.useGetHomeProductV2Query(beneficiosParams, {
        ...queryOptions,
        skip: skipLegacyBeneficios || !isV2Enabled,
    });
    const legacyBenefitsQuery = isV2Enabled ? beneficiosV2 : beneficiosV1;
    const legacyBenefitsData = legacyBenefitsQuery.data &&
        typeof legacyBenefitsQuery.data === 'object' &&
        legacyBenefitsQuery.data !== null &&
        'result' in legacyBenefitsQuery.data &&
        legacyBenefitsQuery.data.result
        ? normalizeProductResponse(legacyBenefitsQuery.data.result)
        : undefined;
    const hasLegacyBenefitsContract = legacyBenefitsData?.status === ApiProductStatus.ACTIVE ||
        legacyBenefitsData?.status === ApiProductStatus.ACTIVATING;
    const hasNoLegacyBenefitsContract = legacyBenefitsData?.status === ApiProductStatus.AVAILABLE;
    const shouldQueryEmissorBenefits = isEmissorEnabled && hasNoLegacyBenefitsContract;
    const skipEmissorBenefits = !enabled || !selectedAccountId || !selectedCompanyDocument || !hasBenefitPermission || !shouldQueryEmissorBenefits;
    const accountantEmissorBenefits = useGetAccountantEmissorHomeBenefitsProductQuery({ document: selectedCompanyDocument || '' }, {
        ...queryOptions,
        skip: skipEmissorBenefits || appDomain !== AppDomain.ESCRITORIO,
    });
    const clientEmissorBenefits = useGetClientEmissorHomeBenefitsProductQuery({ document: selectedCompanyDocument || '' }, {
        ...queryOptions,
        skip: skipEmissorBenefits || appDomain !== AppDomain.EMPRESA,
    });
    const emissorBenefitsQuery = appDomain === AppDomain.ESCRITORIO ? accountantEmissorBenefits : clientEmissorBenefits;
    const beneficiosQuery = shouldQueryEmissorBenefits && !hasLegacyBenefitsContract ? emissorBenefitsQuery : legacyBenefitsQuery;
    useEffect(() => {
        if (contaDigitalQuery.isLoading) {
            dispatch(setProductLoading({
                productAlias: ProductAlias.CONTA_DIGITAL,
                isLoading: true,
            }));
        }
        else if (contaDigitalQuery.error) {
            dispatch(setProductError({
                productAlias: ProductAlias.CONTA_DIGITAL,
                error: 'Falha ao carregar produto Conta Digital',
            }));
        }
        else if (contaDigitalQuery.data &&
            typeof contaDigitalQuery.data === 'object' &&
            contaDigitalQuery.data !== null &&
            'result' in contaDigitalQuery.data) {
            const result = contaDigitalQuery.data.result;
            const normalized = normalizeProductResponse(result);
            dispatch(setProductData({
                productAlias: ProductAlias.CONTA_DIGITAL,
                data: normalized,
            }));
        }
    }, [contaDigitalQuery.isLoading, contaDigitalQuery.error, contaDigitalQuery.data, dispatch]);
    useEffect(() => {
        if (hasBillingPermission) {
            if (cobrancasQuery.isLoading) {
                dispatch(setProductLoading({
                    productAlias: ProductAlias.COBRANCAS,
                    isLoading: true,
                }));
            }
            else if (cobrancasQuery.error) {
                dispatch(setProductError({
                    productAlias: ProductAlias.COBRANCAS,
                    error: 'Falha ao carregar produto Cobranças',
                }));
            }
            else if (cobrancasQuery.data &&
                typeof cobrancasQuery.data === 'object' &&
                cobrancasQuery.data !== null &&
                'result' in cobrancasQuery.data) {
                const result = cobrancasQuery.data.result;
                const normalized = normalizeProductResponse(result);
                dispatch(setProductData({
                    productAlias: ProductAlias.COBRANCAS,
                    data: normalized,
                }));
            }
        }
        else {
            dispatch(setProductLoading({
                productAlias: ProductAlias.COBRANCAS,
                isLoading: false,
            }));
        }
    }, [cobrancasQuery.isLoading, cobrancasQuery.error, cobrancasQuery.data, hasBillingPermission, dispatch]);
    useEffect(() => {
        if (hasDigitalAccountingPermission) {
            if (contabilidadeQuery.isLoading) {
                dispatch(setProductLoading({
                    productAlias: ProductAlias.CONTABILIDADE_DIGITAL,
                    isLoading: true,
                }));
            }
            else if (contabilidadeQuery.error) {
                dispatch(setProductError({
                    productAlias: ProductAlias.CONTABILIDADE_DIGITAL,
                    error: 'Falha ao carregar produto Contabilidade Digital',
                }));
            }
            else if (contabilidadeQuery.data &&
                typeof contabilidadeQuery.data === 'object' &&
                contabilidadeQuery.data !== null &&
                'result' in contabilidadeQuery.data) {
                const result = contabilidadeQuery.data.result;
                const normalized = normalizeProductResponse(result);
                dispatch(setProductData({
                    productAlias: ProductAlias.CONTABILIDADE_DIGITAL,
                    data: normalized,
                }));
            }
        }
        else {
            dispatch(setProductLoading({
                productAlias: ProductAlias.CONTABILIDADE_DIGITAL,
                isLoading: false,
            }));
        }
    }, [
        contabilidadeQuery.isLoading,
        contabilidadeQuery.error,
        contabilidadeQuery.data,
        hasDigitalAccountingPermission,
        dispatch,
    ]);
    useEffect(() => {
        if (hasProgramaFidelidadePermission) {
            if (programaFidelidadeQuery.isLoading) {
                dispatch(setProductLoading({
                    productAlias: ProductAlias.DOMINIO_RECOMPENSAS,
                    isLoading: true,
                }));
            }
            else if (programaFidelidadeQuery.error) {
                dispatch(setProductError({
                    productAlias: ProductAlias.DOMINIO_RECOMPENSAS,
                    error: 'Falha ao carregar produto Programa Fidelidade',
                }));
            }
            else if (programaFidelidadeQuery.data &&
                typeof programaFidelidadeQuery.data === 'object' &&
                programaFidelidadeQuery.data !== null &&
                'result' in programaFidelidadeQuery.data) {
                const result = programaFidelidadeQuery.data.result;
                const normalized = normalizeProductResponse(result);
                dispatch(setProductData({
                    productAlias: ProductAlias.DOMINIO_RECOMPENSAS,
                    data: normalized,
                }));
            }
        }
        else {
            dispatch(setProductLoading({
                productAlias: ProductAlias.DOMINIO_RECOMPENSAS,
                isLoading: false,
            }));
        }
    }, [
        programaFidelidadeQuery.isLoading,
        programaFidelidadeQuery.error,
        programaFidelidadeQuery.data,
        hasProgramaFidelidadePermission,
        dispatch,
    ]);
    useEffect(() => {
        if (hasBenefitPermission) {
            if (beneficiosQuery.isLoading) {
                dispatch(setProductLoading({
                    productAlias: ProductAlias.BENEFICIOS,
                    isLoading: true,
                }));
            }
            else if (beneficiosQuery.error) {
                dispatch(setProductError({
                    productAlias: ProductAlias.BENEFICIOS,
                    error: 'Falha ao carregar produto Benefícios',
                }));
            }
            else if (beneficiosQuery.data &&
                typeof beneficiosQuery.data === 'object' &&
                beneficiosQuery.data !== null &&
                'result' in beneficiosQuery.data &&
                beneficiosQuery.data.result) {
                const result = beneficiosQuery.data.result;
                const normalized = normalizeProductResponse(result);
                dispatch(setProductData({
                    productAlias: ProductAlias.BENEFICIOS,
                    data: normalized,
                }));
            }
        }
        else {
            dispatch(setProductLoading({
                productAlias: ProductAlias.BENEFICIOS,
                isLoading: false,
            }));
        }
    }, [beneficiosQuery.isLoading, beneficiosQuery.error, beneficiosQuery.data, hasBenefitPermission, dispatch]);
    return {
        products: {
            [ProductAlias.CONTA_DIGITAL]: {
                data: products[ProductAlias.CONTA_DIGITAL].data,
                isLoading: products[ProductAlias.CONTA_DIGITAL].isLoading,
                error: products[ProductAlias.CONTA_DIGITAL].error,
            },
            [ProductAlias.COBRANCAS]: {
                data: products[ProductAlias.COBRANCAS].data,
                isLoading: products[ProductAlias.COBRANCAS].isLoading,
                error: products[ProductAlias.COBRANCAS].error,
            },
            [ProductAlias.CONTABILIDADE_DIGITAL]: {
                data: products[ProductAlias.CONTABILIDADE_DIGITAL].data,
                isLoading: products[ProductAlias.CONTABILIDADE_DIGITAL].isLoading,
                error: products[ProductAlias.CONTABILIDADE_DIGITAL].error,
            },
            [ProductAlias.BENEFICIOS]: {
                data: products[ProductAlias.BENEFICIOS].data,
                isLoading: products[ProductAlias.BENEFICIOS].isLoading,
                error: products[ProductAlias.BENEFICIOS].error,
            },
            [ProductAlias.DOMINIO_RECOMPENSAS]: {
                data: products[ProductAlias.DOMINIO_RECOMPENSAS].data,
                isLoading: products[ProductAlias.DOMINIO_RECOMPENSAS].isLoading,
                error: products[ProductAlias.DOMINIO_RECOMPENSAS].error,
            },
        },
        isLoading,
        hasError,
    };
};
